import ClearIcon from '@mui/icons-material/Clear';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SearchIcon from '@mui/icons-material/Search';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';

import {
  Backdrop,
  Badge,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  Stack,
  TextField,
  alpha,
  useTheme,
} from '@mui/material';
import { MediaListItem } from 'components/common/MediaListItem';
import { TrackCover } from 'components/track/TrackCover';
import { useDebounce } from 'hooks/useDebounce';
import { useUser } from 'hooks/useUser';
import { AppleSearchResult } from 'models/Apple';
import { Track, TrackVendor } from 'models/Track';
import { TrackRequest, TrackRequestType } from 'models/TrackRequest';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { requestHash } from 'utils/requestHash';
export interface SearchTrackProps {
  canRequest: boolean;
  requests?: TrackRequest[];
  onRequest?: (request: TrackRequest) => void;
  onRemoveRequest?: (request: TrackRequest) => void;
  onOpenSearchResults?: () => void;
  onCloseSearchResults?: () => void;
  onPreview?: (track: Track) => void;
  onClosePreview?: () => void;
}

export function SearchTrack({
  canRequest,
  onClosePreview,
  onCloseSearchResults,
  onOpenSearchResults,
  onPreview,
  onRemoveRequest,
  onRequest,
  requests,
}: SearchTrackProps) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  // const [searchTermHash, setSearchTermHash] = useState('');
  const [searchTermRequested, setSearchTermRequested] = useState(false);
  const [searchTermRequest, setSearchTermRequest] = useState<TrackRequest<string> | null>(null);
  const { requesterId } = useUser();
  const theme = useTheme();

  const [results, setResults] = useState<TrackRequest<Track>[]>([]);
  const maxResultsPerVendor = 5;
  // const searchDelay = 200;
  // const canDeleteUntilSeconds = 300;
  // const musicPlayer = useMusicPlayer();
  // const searchTermRequested =
  //   searchTerm && requests?.find((req) => req.hash == searchTermHash);
  const [popperAnchorEl, setPopperAnchorEl] = React.useState<null | Element>(null);
  const [searchResultOpen, setSearchResultOpen] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const searchTermDebounced = useDebounce<string>(searchTerm, 300);

  // TODO: check permissions (localeStorage.userId)
  // const canRemoveRequests = true;

  // Amazon - REQUIRED ACCESS TOKEN
  // @see https://www.developer.amazon.com/de/docs/music/API_web_overview.html
  const fetchAmazonResults = async () => {
    const res = await fetch(
      `https://music-api.amazon.com/flat_search/?type=catalog_track&keywords=${searchTerm}`,
      {
        headers: {
          'Access-Control-Allow-Origin': '*',
        },
      }
    );
  };

  // Apple
  // @see https://developer.apple.com/library/archive/documentation/AudioVideo/Conceptual/iTuneSearchAPI/Searching.html
  const fetchAppleResults = async (): Promise<TrackRequest<Track>[]> => {
    try {
      const params = new URLSearchParams({
        country: 'DE',
        lang: 'de_de',
        //media: 'music',
        entity: 'musicTrack',
        limit: '5',
        term: searchTerm,
      });

      const res = await fetch(`https://itunes.apple.com/search/?${params.toString()}`, {
        credentials: 'same-origin',
        headers: {
          'Access-Control-Allow-Origin': '*',
        },
      });

      if (res.status == 200) {
        const data = (await res.json()).results?.slice(0, maxResultsPerVendor) as any[];

        const parseResult = (value: AppleSearchResult) =>
          new Promise<TrackRequest<Track>>(async (resolve) => {
            const resultHash = await requestHash(value.trackName, value.artistName);
            const requestedTrack = requests?.find(({ hash }) => hash == resultHash);

            return resolve({
              ...(requestedTrack || {}),
              hash: resultHash,
              type: TrackRequestType.Track,
              value: {
                artist: value.artistName,
                title: value.trackCensoredName,
                titleSimplified: value.trackName,
                collection: value.collectionName,
                duration: Math.ceil(value.trackTimeMillis / 1000),
                price: value.trackPrice,
                vendor: TrackVendor.Apple,
                vendorUrl: value.trackViewUrl,
                vendorArtistUrl: value.artistViewUrl,
                vendorCollectionUrl: value.collectionViewUrl,
                previewUrl: value.previewUrl,
                artworkUrl: value.artworkUrl100,
              },
            });
          });

        return Promise.all(data.map(parseResult));
      }
    } catch (error) {
      console.error(error);
      return [];
    }

    return [];
  };

  // DJcity - ????
  const fetchDjcityResults = async () => {
    const res = await fetch(`https://api.djcity.com/v1/esearch?searchTerm=${searchTerm}`, {
      headers: {
        'Access-Control-Allow-Origin': 'https://localhost:3000',
      },
    });
  };

  // Beatport - REQUIRED ACCESS TOKEN
  // @see https://api.beatport.com/v4/docs/catalog/search
  const fetchBeatportResults = async () => {
    const res = await fetch(`https://api.beatport.com/v4/docs/catalog/search?q=${searchTerm}`, {
      headers: {
        'Access-Control-Allow-Origin': window.origin,
      },
    });
  };

  const fetchSearchResults = async (input: string) => {
    setIsLoadingResults(true);

    if (input) {
      // Remove results with same hash value
      const newResults = (await fetchAppleResults()).filter(
        (obj, index, self) => index === self.findIndex((t) => t.hash === obj.hash)
      );

      // setSearchTermId(await requestHash(input));

      if (newResults) {
        setResults(newResults);
        setIsLoadingResults(false);
      } else {
        setIsLoadingResults(false);
      }
    } else {
      setIsLoadingResults(false);
    }
  };

  const handleTrackRequest = async (request: TrackRequest<Track>) => {
    onRequest?.({
      value: request.value,
      type: TrackRequestType.Track,
      hash: request.hash,
      // count: request.count + 1,
    });

    updateResults();

    // if (results.length) {
    //   setResults(
    //     results.map((result) => {
    //       if (result.hash == request.hash) {
    //         result.count++;
    //       }

    //       return result;
    //     })
    //   );
    // }
  };

  const handleRemoveRequest = (request: TrackRequest<Track>) => {
    onRemoveRequest?.(request);
    updateResults();
  };

  const handleChangeSearchTerm = async (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setSearchTermRequested(false);
    setPopperAnchorEl(e.target.closest('.MuiInputBase-root'));
    setSearchTerm(e.target.value);

    if (e.target.value != '') {
      handleOpenSearchResults();

      const hash = await requestHash(e.target.value);

      const request: TrackRequest<string> = {
        value: e.target.value,
        type: TrackRequestType.String,
        hash: hash,
      };

      setSearchTermRequest(request);

      if (
        requests
          ?.find((request) => request.hash == hash)
          ?.requesters?.some(({ id }) => id === requesterId)
      ) {
        setSearchTermRequested(true);
      }
    }
  };

  const handleStringRequest = async () => {
    // const request: TrackRequest<string> = {
    //   value: value,
    //   type: TrackRequestType.String,
    //   hash: await requestHash(value),
    // };

    if (searchTermRequest) {
      onRequest?.(searchTermRequest);
      setSearchTermRequested(true);
      // setSearchTermRequest(request);
      updateResults();
    }
  };

  const handleRemoveStringRequest = async () => {
    if (searchTermRequest) {
      onRemoveRequest?.(searchTermRequest);
      setSearchTermRequested(false);
      // setSearchTermRequest(request);
      updateResults();
    }
    // const request: TrackRequest<string> = {
    //   value: value,
    //   type: TrackRequestType.String,
    //   hash: await requestHash(value),
    // };
    // onRequest?.(request);
    // setSearchTermRequest(request);
    // updateResults();
  };

  const handleOpenSearchResults = () => {
    onOpenSearchResults?.();
    setSearchResultOpen(true);
  };

  const handleCloseSearchResults = () => {
    onCloseSearchResults?.();
    setSearchResultOpen(false);
  };

  const handleRequestToggle = (track: TrackRequest<Track>, isRequestedByCurrentUser: boolean) => {
    if (canRequest) {
      if (isRequestedByCurrentUser) {
        onRemoveRequest?.(track);
      } else {
        onRequest?.(track);
      }
    }
  };

  const updateResults = () => {
    if (results.length) {
      setResults(
        results.map((result) => {
          const foundRequest = requests?.find((request) => request.hash == result.hash);

          if (foundRequest) {
            // result.count = foundRequest.count;
            result.requesters = foundRequest.requesters;
          }

          return result;
        })
      );
    }
  };

  useEffect(() => {
    updateResults();
  }, [requests]);

  useEffect(() => {
    fetchSearchResults(searchTermDebounced);
  }, [searchTermDebounced]);

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      <Backdrop
        open={searchResultOpen}
        onClick={handleCloseSearchResults}
        invisible
        sx={{
          zIndex: (theme) => theme.zIndex.drawer - 2,
          backdropFilter: 'blur(5px)',
        }}
      />
      <TextField
        label={
          <>
            <SearchIcon fontSize="small" sx={{ verticalAlign: 'center', mr: 0.5 }} />
            {t('searchTrackOrArtist', 'Search Track or Artist')}
          </>
        }
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.drawer - 1 }}
        value={searchTerm}
        onFocus={() => {
          if (searchTerm != '') {
            handleOpenSearchResults();
          }
        }}
        onChange={handleChangeSearchTerm}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => {
                    setSearchTerm('');
                    setResults([]);
                    handleCloseSearchResults();
                  }}
                  edge="end"
                >
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />
      <Popper
        open={searchResultOpen}
        placement="bottom"
        anchorEl={popperAnchorEl}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer - 1,
          width: '90%',
          maxWidth: popperAnchorEl?.clientWidth,
          pt: 1,
        }}
      >
        <Paper elevation={3} sx={{ position: 'relative', mb: 1 }}>
          <Backdrop
            open={isLoadingResults}
            // onClick={() => setSearchResultOpen(false)}
            sx={{
              zIndex: (theme) => theme.zIndex.drawer,
              position: 'absolute',
            }}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
          {results?.map((result, index) => {
            const track = result.value;
            const isRequestedByCurrentUser = !!result.requesters?.some(
              ({ id }) => id === requesterId
            );
            const requestCount = result.requesters?.length ?? 0;

            const background = isRequestedByCurrentUser
              ? `linear-gradient(90deg, ${alpha(
                  theme.palette.primary.main,
                  0.12
                )} 0%, transparent 100%)`
              : 'transparent';

            const badgeContent = isRequestedByCurrentUser ? (
              <ThumbUpIcon sx={{ fontSize: 14 }} />
            ) : null;

            return (
              <MediaListItem
                size="small"
                key={index}
                media={
                  <Badge
                    badgeContent={badgeContent}
                    color="primary"
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                  >
                    <TrackCover title={track.title} cover={track.artworkUrl} />
                  </Badge>
                }
                title={track.title}
                pretitle={track.artist}
                onClick={() => handleRequestToggle(result, isRequestedByCurrentUser)}
                sx={{ background, borderRadius: 0, marginBlock: 1 }}
                trailing={
                  onPreview && (
                    <IconButton onClick={() => onPreview?.(track)} disabled={!canRequest}>
                      <PlayArrowIcon />
                    </IconButton>
                  )
                }
                subtitle={
                  <Stack direction="row" gap={1}>
                    <Stack direction="row" gap={0.5}>
                      <ThumbUpIcon sx={{ fontSize: 14 }} />
                      {requestCount}
                    </Stack>
                  </Stack>
                }
              />
            );
          })}
        </Paper>
        {searchTerm != '' && canRequest && (
          <Paper elevation={3} sx={{ position: 'relative' }}>
            <MediaListItem
              size="small"
              title={t('requestByTextTerm', 'Request "{{searchTerm}}"', {
                searchTerm: searchTerm,
              })}
              subtitle={t('requestByTextTermHope', 'Hope your DJ will find it', {
                searchTerm: searchTerm,
              })}
              sx={{
                background: searchTermRequested
                  ? `linear-gradient(90deg, ${alpha(
                      theme.palette.primary.main,
                      0.12
                    )} 0%, transparent 100%)`
                  : 'transparent',
              }}
              media={
                <Badge
                  badgeContent={searchTermRequested ? <ThumbUpIcon sx={{ fontSize: 14 }} /> : null}
                  color="primary"
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                >
                  <TrackCover title={searchTerm} fallback={'TEXT'} />
                </Badge>
              }
              onClick={() =>
                searchTermRequested ? handleRemoveStringRequest() : handleStringRequest()
              }
            />
          </Paper>
        )}
        {/* <Paper elevation={3} sx={{ position: 'relative' }}>
          {searchTerm != '' && canRequest && (
            <Grid2
              container
              sx={{
                overflow: 'hidden',
                // flexWrap: 'nowrap',
                mx: 'auto',
                p: 1,
                '&:hover': {
                  background: 'rgba(0, 0, 0, .2)',
                },
              }}
            >
              <Grid2 sx={{ display: 'flex', alignItems: 'center', flex: '1' }}>
                <Box sx={{ width: '100%' }}>
                  <Typography noWrap sx={{ display: 'block' }}>
                    <Trans
                      i18nKey="requestByStringTerm"
                      values={{ searchTerm: searchTerm }}
                      components={{ strong: <strong /> }}
                      defaults="Request <strong>{{searchTerm}}</strong> and hope your DJ will find it"
                    ></Trans>
                    {t(
                      'requestByStringTerm',
                      'Request "{{searchTerm}}" and hope your DJ will find it',
                      {
                        searchTerm: <b>{searchTerm}</b>,
                      }
                    )}
                  </Typography>
                </Box>
              </Grid2>
              <Grid2 sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton
                  onClick={async () => {
                    searchTermRequested ? handleRemoveStringRequest() : handleStringRequest();
                  }}
                  sx={{ mx: 1 }}
                >
                  {searchTermRequested ? (
                    <FavoriteIcon color="primary" />
                  ) : (
                    <FavoriteBorderOutlinedIcon />
                  )}
                </IconButton>
              </Grid2>
            </Grid2>
          )}
        </Paper> */}
      </Popper>
    </Box>
  );
}
